import type { WasmApi } from "@kaleidoscope-solutions/engine-wasm"
import type { EngineApi } from "@/logic"
import {
  EngineConfig,
  EngineConfigKind
} from "@kaleidoscope-solutions/engine-wasm"

import { apiLoader } from "@/logic"
import messageHandler from "@/logic/message-handler"
import { ModelLogLevel } from "@/utils/useEngineModel"
import worker from "@/worker?worker&url"

export class ModelApi {
  protected _api?: WasmApi
  protected _logLevel: ModelLogLevel

  constructor(logLevel?: ModelLogLevel) {
    this._logLevel = logLevel || ModelLogLevel.Trace
  }

  get api(): WasmApi {
    if (this._api === undefined) {
      throw new Error("API is undefined")
    }
    return this._api
  }

  set api(api: WasmApi) {
    this._api = api
  }

  setLogLevel(logLevel: ModelLogLevel) {
    this._logLevel = logLevel
  }

  async initialise(tokenCallback: () => Promise<string>, workerPath?: string) {
    const sandbox = !!workerPath
    const engineApi: EngineApi = await apiLoader()

    const engineConfig = new EngineConfig()
      .add(
        EngineConfigKind.ApiBaseUrl,
        import.meta.env.VITE_API_URL
      )
      .add(
        EngineConfigKind.ApiModelPersistenceUrl,
        import.meta.env.VITE_API_MODEL_PERSISTENCE_URL
      )
      .add(
        EngineConfigKind.ApiManagementUrl,
        import.meta.env.VITE_API_MANAGEMENT_URL
      )
      .add(
        EngineConfigKind.WorkerPath,
        workerPath ?? worker
      )
    // await is needed here.
    this.api = await engineApi.newApi(engineConfig, tokenCallback, sandbox)

    await this.api.setLogLevel(this._logLevel)
    this.setUpMessagePoll()
  }

  setUpMessagePoll() {
    const POLL_MESSAGES_INTERVAL_MS = 50

    const pollForNewMessages = async () => {
      const messages = await this.api?.pollForNewMessages()

      if (messages && messages.length > 0) {
        for (const message of messages) {
          messageHandler.emit(message.kind, message.content)
        }
      }

      setTimeout(pollForNewMessages, POLL_MESSAGES_INTERVAL_MS)
    }

    setTimeout(pollForNewMessages, POLL_MESSAGES_INTERVAL_MS)
  }

  // Parses a JSON
  parseResult<T>(json: string): T | undefined {
    try {
      const parsed = JSON.parse(json)
      return parsed as T
    } catch {
      return undefined
    }
  }

  terminateWorker() {
    this.api?.terminateWorker()
  }
}
